import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import axiosInstance from '../api/axios';

/**
 * BlogLikeButton — Herz-Button mit Like-Counter für Blog-Beiträge.
 *
 * - Like-State wird in localStorage gespeichert (`blog_liked_{postId}`),
 *   damit Besucher pro Browser nur einmal liken (oder einen Like zurücknehmen) können.
 * - Optimistic UI: Klick wird sofort visuell sichtbar, Server-Request läuft im Hintergrund.
 * - Bei Fehler wird der Zustand zurückgerollt.
 *
 * Props:
 *  - postId: required
 *  - initialLikes: Startwert für den Counter (default 0)
 *  - size: 'sm' | 'md' (default 'md')
 *  - className: optional extra classes
 */
const BlogLikeButton = ({ postId, initialLikes = 0, size = 'md', className = '' }) => {
  const storageKey = `blog_liked_${postId}`;
  const [likes, setLikes] = useState(initialLikes);
  const [liked, setLiked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      setLiked(localStorage.getItem(storageKey) === '1');
    } catch (e) {
      // privacy mode: no localStorage — treat as not liked
    }
  }, [storageKey]);

  // Sync incoming initialLikes when post changes
  useEffect(() => {
    setLikes(initialLikes);
  }, [initialLikes]);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy || !postId) return;

    const wasLiked = liked;
    const optimisticCount = wasLiked ? Math.max(0, likes - 1) : likes + 1;

    // Optimistic
    setLiked(!wasLiked);
    setLikes(optimisticCount);
    setBusy(true);
    try {
      const endpoint = wasLiked ? `/blog/posts/${postId}/unlike` : `/blog/posts/${postId}/like`;
      const res = await axiosInstance.post(endpoint);
      if (typeof res?.data?.likes === 'number') {
        setLikes(res.data.likes);
      }
      try {
        if (wasLiked) localStorage.removeItem(storageKey);
        else localStorage.setItem(storageKey, '1');
      } catch (_) {}
    } catch (err) {
      // Roll back
      setLiked(wasLiked);
      setLikes(likes);
    } finally {
      setBusy(false);
    }
  };

  const isSmall = size === 'sm';
  const heartCls = isSmall ? 'h-4 w-4' : 'h-5 w-5';
  const padding = isSmall ? 'px-2 py-1' : 'px-3 py-1.5';
  const textCls = isSmall ? 'text-xs' : 'text-sm';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-pressed={liked}
      aria-label={liked ? 'Like entfernen' : 'Beitrag liken'}
      className={`inline-flex items-center gap-1.5 ${padding} ${textCls} rounded-full border transition-all duration-200 ${
        liked
          ? 'bg-[#FBE4DA] border-[#E89B82] text-[#B0512E]'
          : 'bg-white border-[#E8DCC8] text-[#5D4E3C] hover:bg-[#FAF5EE]'
      } ${busy ? 'opacity-60 cursor-wait' : 'cursor-pointer'} ${className}`}
      data-testid={`blog-like-button-${postId}`}
      title={liked ? 'Du hast diesen Beitrag geliked' : 'Diesen Beitrag liken'}
    >
      <Heart
        className={`${heartCls} transition-transform ${liked ? 'scale-110' : ''}`}
        fill={liked ? '#E27158' : 'none'}
        strokeWidth={liked ? 0 : 2}
        stroke={liked ? '#E27158' : 'currentColor'}
      />
      <span className="font-medium" data-testid={`blog-like-count-${postId}`}>
        {likes}
      </span>
    </button>
  );
};

export default BlogLikeButton;
